import React, { useState, useEffect } from 'react';
import {
  VStack, Input, Select, Textarea, Text, Box, Button, SimpleGrid,
  FormLabel, FormControl, HStack, Switch, Badge, useToast,
} from '@chakra-ui/react';
import { panteonesApi, localidadesApi, Localidad, Panteon } from '../../../api/serviciosApi';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: any;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Box borderBottom="2px solid" borderColor="#5b182b" pb={1} mb={1} mt={3}>
    <Text fontSize="xs" fontWeight="bold" color="#5b182b" textTransform="uppercase" letterSpacing="wide">{children}</Text>
  </Box>
);

const Field = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Box>
    <Text fontSize="xs" color="gray.500" fontWeight="bold">{label}</Text>
    <Text fontSize="sm">{value || '—'}</Text>
  </Box>
);

const FormularioPanteon: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const [localidades, setLocalidades] = useState<Localidad[]>([]);
  const [guardando, setGuardando] = useState(false);

  const [nombre, setNombre] = useState(data?.nombre || '');
  const [localidad, setLocalidad] = useState(data?.localidad || '');
  const [observaciones, setObservaciones] = useState(data?.observaciones || '');
  const [activo, setActivo] = useState<boolean>(data?.activo ?? true);

  useEffect(() => {
    localidadesApi.list().then(setLocalidades).catch(() => setLocalidades([]));
  }, []);

  const handleGuardar = async () => {
    if (!nombre) {
      toast({ title: 'Campo obligatorio', description: 'El nombre del panteón es requerido.', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const loc = localidades.find((l) => l.descripcion === localidad);
      const body: Partial<Panteon> = {
        nombre, localidad: localidad || undefined, localidadId: loc?.id,
        observaciones, activo,
      };
      if (modo === 'edit') await panteonesApi.update(data.id, body);
      else await panteonesApi.create(body);
      toast({ title: modo === 'edit' ? 'Panteón actualizado' : 'Panteón registrado', status: 'success', duration: 3000 });
      onGuardado?.();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'Error al guardar el panteón.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  if (modo === 'view') {
    const d = data || {};
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Datos del Panteón</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Field label="NOMBRE" value={d.nombre} />
          <Field label="LOCALIDAD" value={d.localidad} />
          <Field label="ACTIVO" value={<Badge colorScheme={d.activo ? 'green' : 'gray'}>{d.activo ? 'Sí' : 'No'}</Badge>} />
        </SimpleGrid>
        {d.observaciones && (<><SectionTitle>Observaciones</SectionTitle><Text fontSize="sm" whiteSpace="pre-wrap">{d.observaciones}</Text></>)}
      </VStack>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Datos del Panteón</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl isRequired gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Nombre del panteón</FormLabel>
          <Input placeholder="Ej. Panteón Municipal San Juan" value={nombre} onChange={(e) => setNombre(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Localidad</FormLabel>
          <Select placeholder="Selecciona localidad" value={localidad} onChange={(e) => setLocalidad(e.target.value)}>
            {localidades.map((l) => <option key={l.id} value={l.descripcion}>{l.descripcion}</option>)}
          </Select>
        </FormControl>
        <FormControl display="flex" alignItems="center" gap={3} pt={{ md: 6 }}>
          <FormLabel fontSize="sm" mb={0}>Activo</FormLabel>
          <Switch colorScheme="red" isChecked={activo} onChange={(e) => setActivo(e.target.checked)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Observaciones</FormLabel>
          <Textarea rows={3} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
        </FormControl>
      </SimpleGrid>
      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">Guardar</Button>
      </HStack>
    </VStack>
  );
};

export default FormularioPanteon;
